import getMenuItems from "@/api/requests/wordpress/getMenuItems";
import getPages from "@/api/requests/wordpress/getPages";
import getYoastRedirect from "@/api/requests/wordpress/getYoastRedirect";
import { MenuItems, Page, RegionRestriction } from "@/custom-types/Wordpress";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import { getUrlPath } from "@/utils/getUrlPath";
import {
  redirectToPreviewPage,
  regionRestrictionRedirect,
} from "@/utils/news/redirect";
import { getCountryCodeHeader } from "@/utils/requestContext";

import ArticleContent from "@/components/news/ArticleContent";
import PageLayout from "@/components/news/Layouts/PageLayout";

type LearnPageProps = {
  page: Page;
  menuItems: MenuItems;
};

const LEARN_PAGE_ID = 134989;

const LearnPage: React.FC<LearnPageProps> = ({ page, menuItems }) => {
  return (
    <PageLayout menuItems={menuItems} page={page}>
      <ArticleContent item={page} />
    </PageLayout>
  );
};

export const getServerSideProps = createGetServerSideProps<
  LearnPageProps,
  {
    slugs: string[];
  }
>(async (context) => {
  const {
    params: { slugs } = {
      slugs: [""],
    },
    query: { preview_id, preview, page_id },
    resolvedUrl,
  } = context;
  const redirect = await getYoastRedirect(getUrlPath(resolvedUrl));

  if (redirect?.destination) {
    return {
      redirect,
    };
  }

  const isPreview =
    (preview_id || page_id) && (preview === "true" || preview === "1");
  if (isPreview) {
    return redirectToPreviewPage((preview_id || page_id) as string, "page");
  }

  const countryCode = getCountryCodeHeader(context.req.headers);

  const { pages } = await getPages(
    countryCode,
    { slug: `${slugs[slugs.length - 1]}` },
    "1",
    "3",
  );

  let page;
  if (pages?.length > 1) {
    page = pages.find((p) => p.topLevelParent === LEARN_PAGE_ID);
  } else if (pages?.length === 1) {
    page = pages?.[0];
  }

  if (!page) {
    return {
      notFound: true,
    };
  }

  const { regionRestriction, regionRestrictionRedirectUrl } = page;
  if (
    regionRestrictionRedirectUrl ||
    (regionRestriction !== RegionRestriction.WW &&
      regionRestriction !== countryCode)
  ) {
    return regionRestrictionRedirect(
      resolvedUrl,
      regionRestriction,
      regionRestrictionRedirectUrl,
    );
  }

  const menuItems = await getMenuItems("learn");

  return {
    props: {
      menuItems,
      page,
    },
  };
});

export default LearnPage;
